/**
 * BrandLogo — the workspace's identity mark, shared across every surface
 * that renders it before or after sign-in (the storefront landing header,
 * the auth sign-in card). Reads workspaceCard directly so it re-renders
 * once the public workspace card resolves after mount. The brand's asset
 * where it has one; no identity while the card is reading. A resolved
 * card without an asset uses the platform mark or, on a surface marked
 * `named` where the brand's name must be read, its wordmark.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import "../storefront/revia-logo.view.ts";
import { css, html, nothing } from "lit";
import { property } from "lit/decorators.js";
import { brandLoading, brandLogo, openBrandName } from "./workspace.state.ts";

@schmancyElement("brand-logo")
export class BrandLogo extends SchmancyElement {
	// Block, not the inline default: the mask div below sizes itself from
	// whichever axis the host makes definite, and a percentage against an
	// inline box's shrink-to-fit width never resolves in normal flow. Either
	// axis drives: constrain the host's width (`w-28`) and the mask's
	// aspect-ratio derives its height; constrain the host's height (`h-10`,
	// to match a control it sits beside) and the ratio derives the width the
	// host then shrink-to-fits to.
	static styles = [
		css`
			:host {
				display: block;
			}
			/* The bubble is square and never warps. A width or height the
			   consumer sets wins over this default; 100cqmin below is the
			   smaller of the two axes, so a box set wrongly letterboxes the
			   mark rather than stretching it. Size containment only here: the
			   logo's box derives from its own aspect-ratio content. */
			:host([bubble]) {
				height: 2.5rem;
				width: fit-content;
				aspect-ratio: 1;
				flex-shrink: 0;
				container-type: size;
			}
		`,
	];

	/** The brand's name must be read here, so a brand with no logo spells it. */
	@property({ type: Boolean }) named = false;

	override willUpdate() {
		this.toggleAttribute(
			"bubble",
			brandLogo.get() === null &&
				(brandLoading.get() || !this.named || openBrandName.get() === null),
		);
	}

	override render() {
		// An unanswered card may carry a client's logo; the platform's mark
		// must never stand in for that client while its identity is loading.
		if (brandLoading.get()) return nothing;
		const logo = brandLogo.get();
		const name = openBrandName.get();
		if (logo) {
			// The mark is a lockup — "WILHELM" over a much smaller second line —
			// whose ink is not evenly distributed down its box: measured off the
			// asset's own alpha channel (1500x326) the mass centroid sits at 0.3565
			// of the box height, so a box-centred mark reads ~4.6px low against the
			// wordmark a reader's eye takes as the line. The percentage translate
			// resolves against the element's own height, so the one number holds at
			// every size and host padding — and it sits here rather than on the host
			// because the no-logo branch below is a single-line wordmark whose
			// optical centre already is its box centre. After it, plain
			// `items-center` aligns the mark in every consumer.
			//
			// The mark is one monochrome asset with a transparent background; masking it
			// with the theme's on-surface color (dark on light, light on dark) — instead of
			// painting it as an <img> — is what makes it legible after a scheme switch with
			// no second light/dark asset to maintain.
			return html`<div
				role="img"
				aria-label=${name}
				class="h-full w-full aspect-[1500/326] translate-y-[14.35%] bg-surface-on"
				style="mask-image: url('${logo}'); -webkit-mask-image: url('${logo}'); mask-size: contain; -webkit-mask-size: contain; mask-repeat: no-repeat; -webkit-mask-repeat: no-repeat; mask-position: left center; -webkit-mask-position: left center;"
			></div>`;
		}
		// Nothing to spell — the bare host — leaves the product's own mark.
		return !this.named || name === null
			? html`<revia-logo animated unlinked
					aria-hidden="true"
					class="block w-[100cqmin] h-[100cqmin]"
				></revia-logo>`
			: html`<schmancy-typography type="headline" token="md" class="truncate"
					>${name}</schmancy-typography
				>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"brand-logo": BrandLogo;
	}
}
